import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  MenuItem,
} from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { EnergySystemType, ScheduleFragment } from '../../../generated/graphql'
import { theme } from '../../../theme'
import { validators } from '../../../validation/validators'
import { Dialog } from '../../dialog/Dialog'
import { ControlledTextField } from '../../form/ControlledTextField'
import { FormGroup } from '../../form/FormGroup'
import { defaultValuesByType, type FormValues } from './form-values'
import { VentilationForm } from './parameters/VentilationForm'
import { DomesticHotWaterForm } from './parameters/DomesticHotWaterForm'
import { CoolingForm } from './parameters/CoolingForm'
import { HeatLoadForm } from './parameters/HeatLoadForm'
import { ElectricityConsumtionForm } from './parameters/ElectricityConsumptionForm'
import { SpaceHeatingForm } from './parameters/SpaceHeatingForm'

type Props = {
  buildingEnergyModelArea: number
  schedules: ScheduleFragment[]
  allowedEnergySystemTypes: EnergySystemType[]
  open: boolean
  onClose: () => void
  onSubmit: (values: FormValues) => void
  defaultValues: Partial<FormValues>
  isCreate: boolean
  onDelete?: () => void
}

const typeToFormMap: Record<EnergySystemType, React.ElementType> = {
  DomesticHotWater: DomesticHotWaterForm,
  Ventilation: VentilationForm,
  Cooling: CoolingForm,
  HeatLoad: HeatLoadForm,
  ElectricityConsumption: ElectricityConsumtionForm,
  SpaceHeating: SpaceHeatingForm,
}

const maxWidthFromFormMap: Record<
  EnergySystemType,
  false | 'md' | 'xs' | 'sm' | 'lg' | 'xl'
> = {
  DomesticHotWater: 'sm',
  Ventilation: 'md',
  Cooling: 'sm',
  HeatLoad: 'md',
  ElectricityConsumption: 'sm',
  SpaceHeating: 'sm',
}

export function EnergySystemDialog({
  buildingEnergyModelArea,
  schedules,
  allowedEnergySystemTypes,
  open,
  onClose,
  onSubmit,
  defaultValues,
  isCreate,
  onDelete,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { control, handleSubmit, errors, reset } = useForm<FormValues>({
    defaultValues,
  })

  const [etype, seEtype] = useState(
    defaultValues.type ?? EnergySystemType.DomesticHotWater,
  )
  const [saveEnabled, setSaveEnabled] = useState(true)

  const ParametersFormRef = useRef(typeToFormMap[etype])

  const maxWidth: false | 'md' | 'xs' | 'sm' | 'lg' | 'xl' | undefined = etype
    ? maxWidthFromFormMap[etype]
    : undefined

  const title = isCreate
    ? t('buildingEnergyModels.drawer.addEnergySystem')
    : defaultValues.name ?? ''

  const enableSave = (enable: boolean) => setSaveEnabled(enable)

  useEffect(() => {
    if (isCreate) {
      reset(defaultValuesByType(etype))
      ParametersFormRef.current = typeToFormMap[etype]
      setSaveEnabled(true)
    }
  }, [etype]) // eslint-disable-line

  return (
    <Dialog title={title} maxWidth={maxWidth} open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <FormGroup>
            <ControlledTextField
              control={control}
              name="name"
              error={errors.name}
              label={t('energySystem.name')}
              rules={validators.required}
              type="name"
            />
          </FormGroup>
          <FormGroup>
            <ControlledTextField
              control={control}
              name="type"
              error={errors.type}
              label={t('energySystem.type')}
              rules={validators.required}
              disabled={!isCreate}
              onChange={(event) =>
                seEtype(
                  EnergySystemType[
                    event.target.value as keyof typeof EnergySystemType
                  ],
                )
              }
              select
            >
              {allowedEnergySystemTypes.map((energySystemType) => (
                <MenuItem key={energySystemType} value={energySystemType}>
                  {t(`energySystemType.${energySystemType}`)}
                </MenuItem>
              ))}
            </ControlledTextField>
          </FormGroup>
          {ParametersFormRef.current && (
            <>
              <Box my={5}>
                <Divider />
              </Box>
              <ParametersFormRef.current // eslint-disable-line
                buildingEnergyModelArea={buildingEnergyModelArea}
                schedules={schedules}
                control={control}
                errors={errors}
                enableSaving={enableSave}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Box display="flex" flexGrow={1} gridGap={theme.spacing(1)}>
            <Box>
              {onDelete && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onDelete}
                >
                  {t('common.remove')}
                </Button>
              )}
            </Box>
            <Box flexGrow={1} />
            <Button variant="contained" onClick={onClose}>
              {t('common.cancel')}
            </Button>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!saveEnabled}
            >
              {isCreate ? t('common.create') : t('common.saveChanges')}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}
